import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Partners.css';
import avrotechLogo from '../Logo/Favicon.png';

const partners = [
  {
    id: 'avrotech',
    name: 'Avrotech',
    tagline: 'Advanced Industrial Automation',
    location: 'Melbourne, Australia 🇦🇺',
    url: 'https://www.avrotech.au/',
    domain: 'avrotech.au',
    logo: avrotechLogo,
    initials: 'AV',
    color: '#0071e3',
    description:
      'Our Australian partner dedicated to empowering customer-focused businesses with smart, scalable tools — from retail and trades to hospitality and education. Together, we bring simplified operations and elevated experiences to businesses of every kind.',
  },
  {
    id: 'amrtech',
    name: 'AMR Tech Developments',
    tagline: 'Smart Automation Solutions',
    location: 'Singapore 🇸🇬',
    url: null,
    domain: null,
    initials: 'AT',
    color: '#30d158',
    description:
      'Singapore-based specialists in Autonomous Mobile Robots (AMR), warehouse automation, and Industry 4.0 smart factory solutions.',
  },
];

const Partners = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.4, 0.25, 1] } },
  };

  return (
    <section id="partners" className="partners-section" ref={ref}>
      <div className="partners-container">
        {/* Header */}
        <motion.div
          className="partners-header"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.h2 className="partners-title" variants={itemVariants}>
            Our Trusted <span className="partners-highlight">Global Partners</span>
          </motion.h2>

          <motion.p className="partners-subtitle" variants={itemVariants}>
            We collaborate with world-class automation and engineering firms to deliver
            comprehensive solutions that span continents and industries.
          </motion.p>
        </motion.div>

        {/* Partner Cards */}
        <motion.div
          className="partners-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {partners.map((partner) => (
            <motion.div
              key={partner.id}
              className="partner-card"
              variants={itemVariants}
              whileHover={{ y: -8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              {/* Logo area */}
              <div className="partner-logo-area">
                {partner.logo ? (
                  <img
                    src={partner.logo}
                    alt={`${partner.name} logo`}
                    className="partner-logo-img"
                  />
                ) : partner.domain ? (
                  <img
                    src={`https://logo.clearbit.com/${partner.domain}`}
                    alt={`${partner.name} logo`}
                    className="partner-logo-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="partner-logo-fallback"
                  style={{
                    background: `linear-gradient(135deg, ${partner.color}33, ${partner.color}66)`,
                    borderColor: `${partner.color}55`,
                    display: partner.logo || partner.domain ? 'none' : 'flex',
                  }}
                >
                  <span style={{ color: partner.color }}>{partner.initials}</span>
                </div>
              </div>

              <div className="partner-info">
                <h3 className="partner-name">{partner.name}</h3>
                <p className="partner-tagline">{partner.tagline}</p>
                <p className="partner-location">
                  <span className="location-icon">📍</span>
                  {partner.location}
                </p>
                <p className="partner-desc">{partner.description}</p>
              </div>

              <div className="partner-footer">
                <div className="partner-divider" />
                {partner.url ? (
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-link"
                    style={{ '--partner-color': partner.color }}
                  >
                    Visit Website
                    <span className="partner-link-arrow">↗</span>
                  </a>
                ) : (
                  <span className="partner-no-link">Partner in Singapore</span>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom note */}
        <motion.p
          className="partners-note"
          variants={itemVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          Interested in becoming a partner?{' '}
          <button
            className="partners-contact-link"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get in touch with us →
          </button>
        </motion.p>
      </div>
    </section>
  );
};

export default Partners;
